/**
 * Booking Error page (epic7 B7) — distinct, branded error cards for the public
 * booking flow. Driven by an `error.code` so each failure mode gets a tailored
 * message + recovery CTA instead of a single generic "something went wrong".
 *
 *   SPA route: #/error?code=SLOT_CONFLICT&service=...&ref=...&detail=...
 *
 * Codes handled (from CheckoutService / booking pipeline):
 *   SLOT_CONFLICT     — slot taken between view + submit → re-pick / join waitlist
 *   PAYMENT_ERROR     — Stripe declined → "card not charged" + try-again CTA
 *   PLAN_CAP_EXCEEDED — tenant online booking paused → show contact details
 *   CLASS_FULL        — group session full → join waitlist / pick different time
 *   (fallback)        — unknown code → generic recoverable card
 *
 * Public + unauthenticated. CSP/nonce-safe: no inline handlers, no inline style;
 * all wiring is addEventListener. Money is DISPLAY-only — this page never charges.
 *
 * Render contract:
 *   renderBookingError(input?) -> void   (writes into #app; reads #/error query)
 */

// ── Types ───────────────────────────────────────────────────────────────

export type BookingErrorCode =
  | 'SLOT_CONFLICT'
  | 'PAYMENT_ERROR'
  | 'PLAN_CAP_EXCEEDED'
  | 'CLASS_FULL';

export interface BookingErrorInput {
  /** Machine error code; falls back to the generic card when unknown/absent. */
  code?: string;
  /** Service id to deep-link "pick another time" / "join waitlist". */
  serviceId?: string;
  /** Human detail line (e.g. Stripe decline message). */
  detail?: string;
  /** Tenant contact for PLAN_CAP_EXCEEDED (phone/email), if known. */
  contactPhone?: string;
  contactEmail?: string;
  /** Average wait text for CLASS_FULL, if known. */
  averageWait?: string;
}

interface ErrorCard {
  readonly title: string;
  readonly message: string;
  /** Show the "Your card was not charged" assurance line. */
  readonly assuranceNoCharge: boolean;
  /** Ordered CTAs rendered as buttons/links. */
  readonly ctas: readonly ErrorCta[];
}

interface ErrorCta {
  readonly label: string;
  readonly action: 'pick-time' | 'join-waitlist' | 'back-to-services' | 'contact';
  readonly variant: 'primary' | 'secondary' | 'outline';
}

// ── Helpers ─────────────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/** Parse the `?...` segment that follows the hash route (e.g. #/error?code=X). */
function parseHashQuery(): URLSearchParams {
  const hash = window.location.hash;
  const qIndex = hash.indexOf('?');
  return new URLSearchParams(qIndex >= 0 ? hash.slice(qIndex + 1) : '');
}

function readInput(explicit?: BookingErrorInput): Required<BookingErrorInput> {
  const q = parseHashQuery();
  return {
    code: explicit?.code ?? q.get('code') ?? '',
    serviceId: explicit?.serviceId ?? q.get('service') ?? q.get('serviceId') ?? '',
    detail: explicit?.detail ?? q.get('detail') ?? '',
    contactPhone: explicit?.contactPhone ?? q.get('phone') ?? '',
    contactEmail: explicit?.contactEmail ?? q.get('email') ?? '',
    averageWait: explicit?.averageWait ?? q.get('wait') ?? '',
  };
}

function normalizeCode(code: string): BookingErrorCode | 'UNKNOWN' {
  switch (code.toUpperCase()) {
    case 'SLOT_CONFLICT':
    case 'SLOT_TAKEN':
      return 'SLOT_CONFLICT';
    case 'PAYMENT_ERROR':
    case 'PAYMENT_FAILED':
      return 'PAYMENT_ERROR';
    case 'PLAN_CAP_EXCEEDED':
    case 'BOOKING_PAUSED':
      return 'PLAN_CAP_EXCEEDED';
    case 'CLASS_FULL':
    case 'CAPACITY_FULL':
      return 'CLASS_FULL';
    default:
      return 'UNKNOWN';
  }
}

// ── Card definitions ────────────────────────────────────────────────────

function buildCard(code: BookingErrorCode | 'UNKNOWN', input: Required<BookingErrorInput>): ErrorCard {
  switch (code) {
    case 'SLOT_CONFLICT':
      return {
        title: 'That slot was just taken',
        message:
          'Someone else booked this time while you were filling in your details. Please pick another time — or join the waitlist and we will let you know if it frees up.',
        assuranceNoCharge: true,
        ctas: [
          { label: 'Pick another time', action: 'pick-time', variant: 'primary' },
          { label: 'Join waitlist', action: 'join-waitlist', variant: 'outline' },
        ],
      };
    case 'PAYMENT_ERROR':
      return {
        title: "Payment didn't go through",
        message:
          input.detail ||
          'Your bank declined the payment. No charge was made. You can try again with a different card.',
        assuranceNoCharge: true,
        ctas: [
          { label: 'Try a different card', action: 'pick-time', variant: 'primary' },
          { label: 'Back to start', action: 'back-to-services', variant: 'secondary' },
        ],
      };
    case 'PLAN_CAP_EXCEEDED':
      return {
        title: 'Online booking is temporarily paused',
        message:
          'This business has paused online bookings for now. Please get in touch directly and they will be happy to help.',
        assuranceNoCharge: true,
        ctas: [{ label: 'Back to start', action: 'back-to-services', variant: 'secondary' }],
      };
    case 'CLASS_FULL':
      return {
        title: 'This class is full',
        message:
          (input.averageWait
            ? `This session is fully booked. Typical wait for a spot is ${input.averageWait}. `
            : 'This session is fully booked. ') +
          'Join the waitlist and we will notify you if a place opens up, or pick a different time.',
        assuranceNoCharge: true,
        ctas: [
          { label: 'Join waitlist', action: 'join-waitlist', variant: 'primary' },
          { label: 'Pick a different time', action: 'pick-time', variant: 'outline' },
        ],
      };
    default:
      return {
        title: 'Something went wrong',
        message:
          input.detail ||
          'We could not complete your booking. No charge was made. Please try again — if the problem continues, contact the business directly.',
        assuranceNoCharge: true,
        ctas: [{ label: 'Back to start', action: 'back-to-services', variant: 'primary' }],
      };
  }
}

// ── Render ──────────────────────────────────────────────────────────────

function ctaMarkup(cta: ErrorCta, input: Required<BookingErrorInput>): string {
  const cls = `ts-btn ts-btn--${cta.variant} ts-btn--full-width`;
  // Contact CTA renders as mailto/tel link when contact info is present.
  if (cta.action === 'contact') {
    const href = input.contactEmail
      ? `mailto:${encodeURIComponent(input.contactEmail)}`
      : input.contactPhone
        ? `tel:${input.contactPhone.replace(/[^+\d]/g, '')}`
        : '#/services';
    return `<a class="${cls}" href="${escapeHtml(href)}">${escapeHtml(cta.label)}</a>`;
  }
  return `<button type="button" class="${cls}" data-action="${cta.action}">${escapeHtml(cta.label)}</button>`;
}

function renderContactBlock(input: Required<BookingErrorInput>): string {
  if (!input.contactPhone && !input.contactEmail) return '';
  const phone = input.contactPhone
    ? `<a class="ts-link" href="tel:${escapeHtml(input.contactPhone.replace(/[^+\d]/g, ''))}">${escapeHtml(input.contactPhone)}</a>`
    : '';
  const email = input.contactEmail
    ? `<a class="ts-link" href="mailto:${escapeHtml(input.contactEmail)}">${escapeHtml(input.contactEmail)}</a>`
    : '';
  return `
    <div class="ts-booking-error__contact">
      ${phone ? `<p class="ts-booking-error__contact-row"><span aria-hidden="true">&#128222;</span> ${phone}</p>` : ''}
      ${email ? `<p class="ts-booking-error__contact-row"><span aria-hidden="true">&#9993;</span> ${email}</p>` : ''}
    </div>`;
}

export function renderBookingError(explicit?: BookingErrorInput): void {
  const appEl = document.getElementById('app');
  if (!appEl) return;

  const input = readInput(explicit);
  const code = normalizeCode(input.code);
  const card = buildCard(code, input);

  const showContact = code === 'PLAN_CAP_EXCEEDED';
  const ctas = card.ctas.map((c) => ctaMarkup(c, input)).join('');

  appEl.innerHTML = `
    <div class="ts-page ts-page--error">
      <div class="ts-booking-error" role="alert" aria-live="assertive">
        <div class="ts-booking-error__icon" aria-hidden="true">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12.5"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <h1 class="ts-booking-error__title">${escapeHtml(card.title)}</h1>
        <p class="ts-booking-error__message">${escapeHtml(card.message)}</p>
        ${card.assuranceNoCharge ? '<p class="ts-booking-error__assurance"><strong>Your card was not charged.</strong></p>' : ''}
        ${showContact ? renderContactBlock(input) : ''}
        <div class="ts-booking-error__actions">
          ${ctas}
        </div>
      </div>
    </div>
  `;

  attachListeners(appEl, input);
}

function attachListeners(appEl: HTMLElement, input: Required<BookingErrorInput>): void {
  for (const btn of appEl.querySelectorAll<HTMLButtonElement>('[data-action]')) {
    btn.addEventListener('click', () => {
      const action = btn.dataset['action'];
      switch (action) {
        case 'pick-time':
          window.location.hash = input.serviceId ? `#/book/${input.serviceId}` : '#/services';
          break;
        case 'join-waitlist':
          window.location.hash = input.serviceId
            ? `#/waitlist?service=${encodeURIComponent(input.serviceId)}`
            : '#/waitlist';
          break;
        case 'back-to-services':
          window.location.hash = '#/services';
          break;
      }
    });
  }
}
